import React, { useMemo, useState } from 'react'
import { Modal } from '@components/common/modal'
import { FlexContainer, YSpacing } from '@components/common'
import {
  Dropdown,
  LabelInfo,
  CurrencyInput,
  Checkbox,
  Button,
} from '@components/common/form'

import {
  BudgetPerPersonSettings,
  ClientGlobalSettings,
  ClientSettings,
} from '@types'

interface BudgetPerLocationModalProps {
  isOpen: boolean
  onClose: () => void
  clientGlobalSettings: ClientGlobalSettings
  clientSettingsCollection: ClientSettings[]
  saveGlobalSettings: (
    clientGlobalSettings: ClientGlobalSettings,
  ) => Promise<ClientGlobalSettings>
  displayFailureMessage: (message: string) => void
}

const BudgetPerLocationModal: React.FC<BudgetPerLocationModalProps> = ({
  onClose,
  clientGlobalSettings,
  clientSettingsCollection,
  saveGlobalSettings,
  displayFailureMessage,
}) => {
  const [selectedLocation, setSelectedLocation] = useState<string>('')
  const [budgetPerPersonSettings, setBudgetPerPersonSettings] =
    useState<BudgetPerPersonSettings>(
      clientGlobalSettings.budgetPerPersonSettings ||
        ({} as BudgetPerPersonSettings),
    )
  const [isCustomBudgetPerPerson, setIsCustomBudgetPerPerson] =
    useState<boolean>(clientGlobalSettings.isCustomBudgetPerPerson)

  const locations = useMemo(() => {
    return clientSettingsCollection
      .filter(({ id }) => id !== undefined && id !== '')
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((settings) => ({
        value: settings.id,
        label:
          settings.name === '' ? 'Unnamed Default Location' : settings.name,
      }))
  }, [clientSettingsCollection])

  const pBudgetSetting = (locationId: string) => {
    const { budgetPerPersonSettings: currentBudgetPerPersonSettings } =
      clientGlobalSettings

    if (!currentBudgetPerPersonSettings[locationId]) {
      return 'N/A'
    }

    const { value, type } = currentBudgetPerPersonSettings[locationId]

    const typeText =
      type === 'allIn' ? 'All In (Includes Fees)' : 'Food Only (Excludes Fees)'

    return `$${value.toFixed(2)} - ${typeText}`
  }

  const onSave = async () => {
    if (
      Object.entries(budgetPerPersonSettings).some(
        ([_, setting]) => setting.value === undefined || setting.type === '',
      )
    ) {
      displayFailureMessage(
        'Some settings are configured incorrectly. Please ensure both value and type are set for all custom budgets.',
      )

      return
    }

    await saveGlobalSettings({
      ...clientGlobalSettings,
      budgetPerPersonSettings,
      isCustomBudgetPerPerson,
    })
    onClose()
  }

  const onChange =
    (locationId: string) =>
    (field: keyof BudgetPerPersonSettings[string], value: string | number) => {
      const nextSetting = budgetPerPersonSettings[locationId]
        ? { ...budgetPerPersonSettings[locationId] }
        : ({} as BudgetPerPersonSettings[string])

      const update = {
        ...nextSetting,
        [field]: value,
      }

      setBudgetPerPersonSettings({
        ...budgetPerPersonSettings,
        [locationId]: update,
      })
    }

  const renderCustomBudgetPerPerson = () => {
    return (
      <FlexContainer
        flexDirection="column"
        justifyContent="space-between"
        width="100%"
      >
        <Dropdown
          label="Select Location"
          value={selectedLocation}
          onChange={(e) => setSelectedLocation(e.target.value)}
        >
          <option value="">Select Location</option>
          {locations.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Dropdown>
        {selectedLocation !== '' && (
          <FlexContainer
            flexDirection="column"
            justifyContent="space-between"
            width="100%"
          >
            <LabelInfo
              label="Current Setting"
              value={pBudgetSetting(selectedLocation)}
            />
            <YSpacing height="10px" />
            <FlexContainer
              flexDirection="row"
              justifyContent="space-between"
              width="100%"
              alignItems="flex-start"
            >
              <CurrencyInput
                label="Budget Per Person"
                width="40%"
                value={
                  budgetPerPersonSettings[selectedLocation]?.value || undefined
                }
                decimals={2}
                onChange={(v) => onChange(selectedLocation)('value', v)}
              />
              <Dropdown
                label="Budget Per Person Type"
                width="40%"
                value={budgetPerPersonSettings[selectedLocation]?.type || ''}
                onChange={(e) =>
                  onChange(selectedLocation)('type', e.target.value)
                }
              >
                <option value="">Select Type</option>
                <option value="allIn">All In</option>
                <option value="foodOnly">Food Only</option>
              </Dropdown>
            </FlexContainer>
          </FlexContainer>
        )}
      </FlexContainer>
    )
  }

  const renderDefaultBudgetPerPerson = () => {
    const { all } = budgetPerPersonSettings
    const { value, type } = all || {}

    return (
      <FlexContainer
        flexDirection="row"
        justifyContent="space-between"
        width="100%"
      >
        <CurrencyInput
          label="Budget Per Person"
          value={value || undefined}
          decimals={2}
          onChange={(v) => onChange('all')('value', v)}
          width="40%"
        />
        <Dropdown
          label="Budget Per Person Type"
          value={type || ''}
          onChange={(e) => onChange('all')('type', e.target.value)}
          width="40%"
        >
          <option value="">Select Type</option>
          <option value="allIn">All In</option>
          <option value="foodOnly">Food Only</option>
        </Dropdown>
      </FlexContainer>
    )
  }

  return (
    <Modal title="Edit Budget(s) Per Location" hideModal={onClose}>
      <LabelInfo
        label="Editing Budgets For:"
        value={
          isCustomBudgetPerPerson ? 'Individual Locations' : 'All Locations'
        }
      />
      <YSpacing height="10px" />
      <Checkbox
        label="Custom Budget Per Person?"
        labelStyle="font-bold"
        checked={isCustomBudgetPerPerson}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setIsCustomBudgetPerPerson(e.target.checked)
          setBudgetPerPersonSettings({})
        }}
      />
      <YSpacing height="10px" />
      {isCustomBudgetPerPerson
        ? renderCustomBudgetPerPerson()
        : renderDefaultBudgetPerPerson()}
      <Button label="Save" onClick={onSave} />
    </Modal>
  )
}

export default BudgetPerLocationModal
